import React from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { MoreHorizontal, Clock, User, Calendar } from 'lucide-react';
import { TaskItemProps, TaskPriority, TaskStatus, TASK_PRIORITIES, TASK_STATUSES } from './types';

const getPriorityColor = (priority: TaskPriority) => {
  switch (priority?.toLowerCase()) {
    case TASK_PRIORITIES.HIGH:
      return 'bg-rose-100 text-rose-700 border-rose-200';
    case TASK_PRIORITIES.MEDIUM:
      return 'bg-orange-100 text-orange-700 border-orange-200';
    case TASK_PRIORITIES.LOW:
      return 'bg-emerald-100 text-emerald-700 border-emerald-200';
    default:
      return 'bg-gray-100 text-gray-700 border-gray-200';
  }
};

const getStatusColor = (status: TaskStatus) => {
  switch (status?.toLowerCase()) {
    case TASK_STATUSES.COMPLETED:
      return 'text-emerald-600';
    case TASK_STATUSES.IN_PROGRESS:
      return 'text-sky-600';
    case TASK_STATUSES.OVERDUE:
      return 'text-rose-600';
    default:
      return 'text-gray-600';
  }
};

const TaskItem: React.FC<TaskItemProps> = ({
  task,
  onTaskClick = () => {},
  onActionClick = () => {},
  className = '',
}) => {
  if (!task || !task.id) {
    console.warn('TaskItem: task object with id is required');
    return null;
  }

  const patientInitials = task.patient
    ?.split(' ')
    .map(name => name.charAt(0))
    .join('')
    .toUpperCase() || '??';

  const handleActionClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent onTaskClick from firing
    onActionClick(task);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onTaskClick(task);
    }
  };

  return (
    <div
      className={cn(
        'flex items-center justify-between p-4 bg-white border border-gray-100 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer',
        className
      )}
      onClick={() => onTaskClick(task)}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
      aria-label={`Task for ${task.patient}`}
    >
      <div className="flex items-center gap-3 flex-1">
        <Avatar className="h-10 w-10 border">
          <AvatarImage src={task.avatar} alt={`${task.patient}'s avatar`} />
          <AvatarFallback>{patientInitials}</AvatarFallback>
        </Avatar>
        <div className="flex-1">
          <p className="font-semibold text-gray-800">{task.type}</p>
          <div className="flex items-center gap-4 text-sm text-gray-500 mt-1">
            <div className="flex items-center gap-1.5">
              <User className="h-4 w-4" />
              <span>{task.patient}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Calendar className="h-4 w-4" />
              <span>{task.provider}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-4 ml-4">
        <Badge variant="outline" className={cn('font-medium', getPriorityColor(task.priority))}>
          {task.priority}
        </Badge>
        <div className={cn('flex items-center gap-1.5 text-sm font-medium', getStatusColor(task.status))}>
          <Clock className="h-4 w-4" />
          <span>{task.time}</span>
        </div>
        <Button variant="ghost" size="icon" onClick={handleActionClick} aria-label="More actions">
          <MoreHorizontal className="h-5 w-5" />
        </Button>
      </div>
    </div>
  );
};

TaskItem.displayName = 'TaskItem';

export { TaskItem };
